/**
 * Workflow Cache Service
 * Caches workflows to prevent repeated DB fetches on every page load
 */

interface CachedWorkflow {
  id: string;
  userId: string;
  name: string;
  description?: string;
  trigger: string;
  actions: string;
  isActive: string;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface CacheEntry {
  workflows: CachedWorkflow[];
  timestamp: number;
  userId: string;
}

const CACHE_KEY_PREFIX = 'workflow_cache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class WorkflowCacheService {
  private memoryCache: Map<string, CacheEntry> = new Map();

  /**
   * Get cached workflows for a user
   */
  get(userId: string): CachedWorkflow[] | null {
    // Check memory cache first
    const memoryCached = this.memoryCache.get(userId);
    if (memoryCached && Date.now() - memoryCached.timestamp < CACHE_DURATION) {
      console.log('[WorkflowCache] Hit (memory):', userId, `${memoryCached.workflows.length} workflows`);
      return memoryCached.workflows;
    }

    // Check localStorage
    try {
      const key = CACHE_KEY_PREFIX + userId;
      const cached = localStorage.getItem(key);
      if (cached) {
        const entry: CacheEntry = safeJsonParse(cached);
        if (Date.now() - entry.timestamp < CACHE_DURATION) {
          console.log('[WorkflowCache] Hit (localStorage):', userId, `${entry.workflows.length} workflows`);
          // Restore to memory cache
          this.memoryCache.set(userId, entry);
          return entry.workflows;
        } else {
          // Expired, remove it
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('[WorkflowCache] Failed to read from localStorage:', error);
    }

    console.log('[WorkflowCache] Miss:', userId);
    return null;
  }

  /**
   * Cache workflows for a user
   */
  set(userId: string, workflows: CachedWorkflow[]): void {
    const entry: CacheEntry = {
      workflows,
      timestamp: Date.now(),
      userId
    };

    // Store in memory
    this.memoryCache.set(userId, entry);

    // Store in localStorage
    try {
      const key = CACHE_KEY_PREFIX + userId;
      localStorage.setItem(key, JSON.stringify(entry));
      console.log('[WorkflowCache] Cached', workflows.length, 'workflows for', userId);
    } catch (error) {
      console.warn('[WorkflowCache] Failed to write to localStorage:', error);
      // If localStorage is full, try to clear old workflow caches
      this.clearOldCaches();
      try {
        localStorage.setItem(key, JSON.stringify(entry));
      } catch (retryError) {
        console.error('[WorkflowCache] Still failed after cleanup:', retryError);
      }
    }
  }

  /**
   * Invalidate cache for a user (call after create/update/delete)
   */
  invalidate(userId: string): void {
    this.memoryCache.delete(userId);
    try {
      const key = CACHE_KEY_PREFIX + userId;
      localStorage.removeItem(key);
      console.log('[WorkflowCache] Invalidated cache for', userId);
    } catch (error) {
      console.warn('[WorkflowCache] Failed to clear localStorage:', error);
    }
  }

  /**
   * Update a single workflow in cache (optimistic update)
   */
  updateWorkflow(userId: string, workflowId: string, updates: Partial<CachedWorkflow>): void {
    const cached = this.get(userId);
    if (cached) {
      const updatedWorkflows = cached.map(wf => 
        wf.id === workflowId ? { ...wf, ...updates, updatedAt: new Date().toISOString() } : wf
      );
      this.set(userId, updatedWorkflows);
    }
  }

  /**
   * Add a workflow to cache (optimistic create)
   */
  addWorkflow(userId: string, workflow: CachedWorkflow): void {
    const cached = this.get(userId) || [];
    this.set(userId, [workflow, ...cached]);
  }

  /**
   * Remove a workflow from cache (optimistic delete)
   */
  removeWorkflow(userId: string, workflowId: string): void {
    const cached = this.get(userId);
    if (cached) {
      const updatedWorkflows = cached.filter(wf => wf.id !== workflowId);
      this.set(userId, updatedWorkflows);
    }
  }

  /**
   * Clear all caches for all users
   */
  clearAll(): void {
    this.memoryCache.clear();
    try {
      // Clear all workflow cache entries from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('[WorkflowCache] Cleared all caches');
    } catch (error) {
      console.warn('[WorkflowCache] Failed to clear localStorage:', error);
    }
  }

  /**
   * Clear old/expired caches to free up localStorage space
   */
  private clearOldCaches(): void {
    try {
      const now = Date.now();
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry = safeJsonParse(cached);
              if (now - entry.timestamp >= CACHE_DURATION) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // Invalid JSON, remove it
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        console.log('[WorkflowCache] Cleared', keysToRemove.length, 'expired caches');
      }
    } catch (error) {
      console.warn('[WorkflowCache] Failed to clear old caches:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { memoryCacheSize: number; localStorageCacheSize: number } {
    let localStorageCacheSize = 0;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX)) {
          localStorageCacheSize++;
        }
      }
    } catch (error) {
      console.warn('[WorkflowCache] Failed to get stats:', error);
    }

    return {
      memoryCacheSize: this.memoryCache.size,
      localStorageCacheSize
    };
  }
}

export const workflowCacheService = new WorkflowCacheService();
